import { useEffect, useRef, useState } from "react";
import { TypeH1 } from "../../atoms/fonts/Types";
import { IconArrowRight } from "../../atoms/icons/Icons";
import { StandarButton } from "../../atoms/util/buttons/Buttons";
import { ParticleField } from "../../atoms/util/particles/ParticleField";
import { useT } from "../../../i18n/LanguageContext";
import "./HeroContent.css";

const CYCLE_MS = 6800;

// The big word fades from the logo's light blue into its mid blue.
const LIGHT = [226, 239, 247];
const MID = [104, 165, 212];
const letterColor = (i, n) => {
	const k = n > 1 ? i / (n - 1) : 0;
	return `rgb(${LIGHT.map((c, j) => Math.round(c + (MID[j] - c) * k)).join(",")})`;
};

function Letters({ word, leaving }) {
	const chars = Array.from(word);
	return (
		<span className={`heroContent_word ${leaving ? "-leaving" : ""}`}>
			{chars.map((ch, i) => (
				<span
					key={i}
					className="heroContent_letter"
					style={{ "--i": i, color: letterColor(i, chars.length) }}
				>
					{ch === " " ? " " : ch}
				</span>
			))}
		</span>
	);
}

export function HeroContent() {
	const { t, lang } = useT();
	const services = t("hero.services");
	const list = Array.isArray(services) ? services : [];
	const anchorRef = useRef(null);

	// -1 while the particles form the logo, then an index into `list`.
	const [active, setActive] = useState(-1);
	const [auto, setAuto] = useState(true);
	const [prev, setPrev] = useState(null);
	const lastWord = useRef(null);

	// The field reports when the logo has formed; the tour starts then.
	const startTour = () => setActive((a) => (a < 0 ? 0 : a));

	useEffect(() => {
		if (!auto || active < 0 || !list.length) return undefined;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
		const id = setTimeout(() => setActive((a) => (a + 1) % list.length), CYCLE_MS);
		return () => clearTimeout(id);
	}, [active, auto, list.length]);

	const index = Math.max(active, 0);
	const word = list[index]?.word || "";
	const shape = active < 0 ? "logo" : list[active]?.key || "website";

	// Keep the outgoing word around just long enough to animate it away.
	useEffect(() => {
		const before = lastWord.current;
		lastWord.current = word;
		if (!before || before === word) return undefined;
		setPrev(before);
		const id = setTimeout(() => setPrev(null), 700);
		return () => clearTimeout(id);
	}, [word]);

	return (
		<>
			<ParticleField shape={shape} anchorRef={anchorRef} onIntroDone={startTour} />

			<div className="heroContent">
				<div className="heroContent_stage" ref={anchorRef} aria-hidden="true" />

				<TypeH1 className="heroContent_title">
					<span className="heroContent_sr">{t("hero.srTitle")}</span>
					<span className="heroContent_words" aria-hidden="true">
						{prev && <Letters key={`out-${prev}-${lang}`} word={prev} leaving />}
						<Letters key={`in-${word}-${lang}`} word={word} />
					</span>
					<span className="heroContent_tail" aria-hidden="true" key={lang}>
						{t("hero.tail")}
					</span>
				</TypeH1>

				<p className="heroContent_subtitle">{t("hero.subtitle")}</p>

				<div className="heroContent_cta">
					<StandarButton
						variant={1}
						label={t("hero.ctaPrimary")}
						href="#contact"
						icon={<IconArrowRight />}
					/>
					<StandarButton variant={3} label={t("hero.ctaSecondary")} href="#services" />
				</div>

				<div className="heroContent_picker" role="tablist" aria-label={t("hero.pickerLabel")}>
					{list.map((s, i) => (
						<button
							key={s.key}
							type="button"
							role="tab"
							aria-selected={i === active}
							className={`heroContent_chip ${i === active ? "is-active" : ""} ${
								i === active && auto ? "is-timing" : ""
							}`}
							style={{ "--cycle": `${CYCLE_MS}ms` }}
							onClick={() => {
								setAuto(false);
								setActive(i);
							}}
						>
							{s.word}
							<span className="heroContent_timer" aria-hidden="true" />
						</button>
					))}
				</div>

			</div>
		</>
	);
}
